import { CLIPBOARD_KIND, CLIPBOARD_VERSION } from './copyPasteConstants';

const warn = (reason, detail) => {
  // eslint-disable-next-line no-console
  console.warn('[clipboardService]', {
    reason,
    detail,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
  });
};

const REQUIRED_STEP_KEYS = ['tool', 'args', 'stepDescription', 'customStep'];

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Returns the envelope iff strictly valid; otherwise null (and logs the reason). */
function validateEnvelope(parsed) {
  if (!isPlainObject(parsed)) { warn('wrong-type', 'envelope not an object'); return null; }
  if (parsed.kind !== CLIPBOARD_KIND) { warn('wrong-kind', parsed.kind); return null; }
  if (parsed.version !== CLIPBOARD_VERSION) {
    warn('future-version', parsed.version);
    return null;
  }
  if (!Array.isArray(parsed.steps)) { warn('wrong-type', 'steps not array'); return null; }
  for (let i = 0; i < parsed.steps.length; i++) {
    const s = parsed.steps[i];
    if (!isPlainObject(s)) { warn('wrong-type', `step[${i}] not object`); return null; }
    for (const k of REQUIRED_STEP_KEYS) {
      if (!(k in s)) { warn('missing-required-field', `step[${i}].${k}`); return null; }
    }
    if (typeof s.tool !== 'string') { warn('wrong-type', `step[${i}].tool`); return null; }
    if (!isPlainObject(s.args)) { warn('wrong-type', `step[${i}].args`); return null; }
    if (typeof s.stepDescription !== 'string') { warn('wrong-type', `step[${i}].stepDescription`); return null; }
    if (s.customStep !== true) { warn('missing-required-field', `step[${i}].customStep !== true`); return null; }
  }
  return parsed;
}

/**
 * Serialize and write an envelope to the system clipboard.
 * Throws if any step has customStep !== true (defensive; caller should have gated this).
 * Throws if navigator.clipboard.writeText rejects.
 */
export async function copySteps(stepsArray, sourceTestCaseId) {
  for (let i = 0; i < stepsArray.length; i++) {
    if (stepsArray[i]?.customStep !== true) {
      throw new Error(`copySteps: step[${i}] has customStep !== true; refusing to write`);
    }
  }
  const envelope = {
    kind: CLIPBOARD_KIND,
    version: CLIPBOARD_VERSION,
    source: { testCaseId: sourceTestCaseId, copiedAt: new Date().toISOString() },
    steps: stepsArray.map(s => ({
      tool: s.tool,
      args: s.args,
      // Step description: prefer the canonical field but fall through to the
      // alternates the studio uses for some step shapes (custom browser_evaluate
      // steps in particular live without a top-level stepDescription). Always
      // emit a string so the read-side schema check passes.
      stepDescription: typeof s.stepDescription === 'string'
        ? s.stepDescription
        : (typeof s.description === 'string' ? s.description
          : (typeof s.extractedData?.stepDescription === 'string'
            ? s.extractedData.stepDescription
            : '')),
      executionMode: s.executionMode,
      executionStrategies: s.executionStrategies,
      customStep: s.customStep,
    })),
  };
  try {
    await navigator.clipboard.writeText(JSON.stringify(envelope));
  } catch (e) {
    warn('write-rejected', e?.message ?? String(e));
    throw e;
  }
}

/**
 * Read and parse a clipboard envelope. Returns null on any failure
 * (permission denied, parse error, wrong kind, future version, missing-required, non-custom).
 */
export async function readSteps() {
  let text;
  try {
    text = await navigator.clipboard.readText();
  } catch (e) {
    warn('no-permission', e?.message ?? String(e));
    return null;
  }
  if (!text) { warn('parse-error', 'empty'); return null; }
  let parsed;
  try { parsed = JSON.parse(text); } catch (e) { warn('parse-error', e.message); return null; }
  return validateEnvelope(parsed);
}

/** Convenience boolean for enabling Paste menu items without committing to a read. */
export async function hasSteps() {
  return (await readSteps()) !== null;
}
